import {
  Card,
  DateInput,
  DividerWithLabel,
  Field,
  InputGroup,
  NumberInput,
} from '@payaca/ambrosia';
import {
  add as addDuration,
  intervalToDuration,
  sub as subDuration,
} from 'date-fns';
import { produce } from 'immer';
import React, { FC } from 'react';
import { z } from 'zod/v4';

export const DURATION_SCHEMA = z
  .object({
    startDate: z.date(),
    days: z.number().min(0),
    hours: z.number().min(0),
    minutes: z.number().min(0),
    endDate: z.date().refine((date) => date <= new Date(), {
      message: 'End date cannot be in the future',
    }),
  })
  .refine((data) => data.startDate < data.endDate, {
    message: 'End date/time must be after start date/time',
    path: ['endDate'],
  });

type Duration = z.infer<typeof DURATION_SCHEMA>;

export type TProps = {
  duration: Duration;
  onChange: (duration: Duration) => void;
};

const DurationFieldset: FC<TProps> = (props) => {
  const { duration, onChange } = props;

  const handleTimeUnitChange =
    (key: 'days' | 'hours' | 'minutes') => (newValue: number) => {
      const newDuration = produce(duration, (draftDuration) => {
        const startDate = duration.startDate;

        draftDuration[key] = newValue;
        draftDuration.endDate = addDuration(startDate, draftDuration);
      });

      onChange(newDuration);
    };

  const handleStartDateChange = (newStartDate: Date) => {
    const newEndDate = addDuration(newStartDate, duration);

    onChange({
      ...duration,
      startDate: newStartDate,
      endDate: newEndDate,
    });
  };

  const handleEndDateChange = (newEndDate: Date) => {
    if (newEndDate > duration.startDate) {
      const { days, hours, minutes } = intervalToDuration({
        start: duration.startDate,
        end: newEndDate,
      });

      onChange({
        ...duration,
        endDate: newEndDate,
        days: days || 0,
        hours: hours || 0,
        minutes: minutes || 0,
      });
    } else {
      const newStartDate = subDuration(newEndDate, duration);

      onChange({
        ...duration,
        startDate: newStartDate,
        endDate: newEndDate,
      });
    }
  };

  return (
    <>
      {/* Start Date */}
      <Field name="startDate">
        <Field.Label>Start date/time</Field.Label>
        <DateInput
          value={duration.startDate}
          onChange={handleStartDateChange}
          showDateSelect={true}
          showTimeSelect={true}
          timeSettings={{
            showNonBusinessHours: true,
            showAllMinuteOptions: true,
          }}
        />
      </Field>

      {/* Duration Inputs */}
      <h4>Duration</h4>
      <Card>
        <Card.Body className="space-y-4">
          <div className="flex gap-4">
            {/* Days */}
            <Field name="duration.days" className="flex-1">
              <Field.Label>Days</Field.Label>
              <InputGroup>
                <NumberInput
                  as={InputGroup.RawInput}
                  value={duration.days}
                  onChange={(value) =>
                    handleTimeUnitChange('days')(value && value > 0 ? value : 0)
                  }
                  min={0}
                />
                <InputGroup.Text>days</InputGroup.Text>
              </InputGroup>
            </Field>

            {/* Hours */}
            <Field name="duration.hours" className="flex-1">
              <Field.Label>Hours</Field.Label>
              <InputGroup>
                <NumberInput
                  as={InputGroup.RawInput}
                  value={duration.hours}
                  onChange={(value) =>
                    handleTimeUnitChange('hours')(
                      value && value > 0 ? value : 0
                    )
                  }
                  min={0}
                />
                <InputGroup.Text>hours</InputGroup.Text>
              </InputGroup>
            </Field>

            {/* Minutes */}
            <Field name="duration.minutes" className="flex-1">
              <Field.Label>Minutes</Field.Label>
              <InputGroup>
                <NumberInput
                  as={InputGroup.RawInput}
                  value={duration.minutes}
                  onChange={(value) =>
                    handleTimeUnitChange('minutes')(
                      value && value > 0 ? value : 0
                    )
                  }
                  min={0}
                />
                <InputGroup.Text>mins</InputGroup.Text>
              </InputGroup>
            </Field>
          </div>

          <DividerWithLabel label="or" labelPosition="center" />

          {/* End Time */}
          <Field name="duration.endDate">
            <Field.Label>End date/time</Field.Label>
            <DateInput
              value={duration.endDate}
              onChange={handleEndDateChange}
              showDateSelect={true}
              showTimeSelect={true}
              timeSettings={{
                showNonBusinessHours: true,
                showAllMinuteOptions: true,
              }}
            />
          </Field>
        </Card.Body>
      </Card>
    </>
  );
};

export default DurationFieldset;
